// @flow
import * as React from 'react';
import { Box } from 'grommet';
import { FormattedMessage } from 'react-intl';
import PipeIcon from 'esm-web-pro/components/layout/sidePanel/icons/PipeIcon';
import UpperContainer from 'esm-web-pro/components/layout/sidePanel/UpperContainer';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import ContinuousPipeAttributes
    from 'esm-web-pro/containers/pages/network/objects/continuous-pipe/ContinuousPipeAttributes';
import ContinuousPipeActions from 'esm-web-pro/containers/pages/network/objects/continuous-pipe/ContinuousPipeActions';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import ContinuousPipeContent from 'esm-web-pro/containers/pages/network/objects/continuous-pipe/ContinuousPipeContent';
import useHighlightLine from 'esm-web-pro/hooks/useHighlightLine';
import { withLeaflet } from 'react-leaflet';
import type { LeafletContext } from 'react-leaflet/lib/types';
import UseHighlightPoint from 'esm-web-pro/hooks/useHighlightPoint';
import BottomContainer from 'esm-web-pro/components/layout/sidePanel/BottomContainer';
import NavigateBackButton from 'esm-web-pro/components/maps/NavigateBackButton';
import SidePane from 'esm-web-pro/components/layout/SidePane';

type Props = {
    panelId: String,
    object: Object,
    map: Object,
    editor: BaseEditor,
    leaflet: LeafletContext,
    updateObject: Function,
}

const ContinuousPipe = (props: Props) => {
    const {
        object: continuousPipe, map: networkMap, editor, leaflet: { map }, updateObject, panelId,
    } = props;

    const [clickedRow, setClickedRow] = React.useState(null);

    const { start_point: startPoint, end_point: endPoint } = continuousPipe;
    const startPointCoords = startPoint.coordinates.coordinates;
    const endPointCoords = endPoint.coordinates.coordinates;

    let startNumber = startPoint.sequence;
    if (startPoint?.point?.type === 'pop' && startPoint.alias) {
        startNumber = startPoint.alias;
    }
    const startPointCallback = React.useCallback(UseHighlightPoint(startPointCoords, startNumber),
        [startPoint]);
    let endNumber = endPoint.sequence;
    if (endPoint?.point?.type === 'pop' && endPoint.alias) {
        endNumber = endPoint.alias;
    }
    const endPointCallback = React.useCallback(UseHighlightPoint(endPointCoords, endNumber), [endPoint]);

    const pipeCoords = React.useMemo(() => continuousPipe.path.map((c) => [c[1], c[0]]), [continuousPipe]);

    const highlightPipe = React.useCallback(useHighlightLine(pipeCoords, [
        {
            color: continuousPipe.infrastructure_provider.color, dashArray: '20', className: 'route-line',
        },
        {
            dashArray: null, className: '',
        }],
    null), [pipeCoords, continuousPipe]);

    const handleView = React.useCallback(() => {
        setClickedRow(null);
        editor.unregisterAllLoadingCallback();
        editor.registerLoadingCallback(highlightPipe);
        editor.registerLoadingCallback(startPointCallback);
        editor.registerLoadingCallback(endPointCallback);
        if (map) {
            map.highlightedGeometry = {
                bounds: pipeCoords, center: null,
            };
            map.panInsideBounds(pipeCoords);
        }
        editor.reloadFeatures();
    }, [setClickedRow, editor, pipeCoords, map, highlightPipe, endPointCallback, startPointCallback]);

    React.useEffect(() => {
        handleView();

        return () => {
            editor.unregisterAllLoadingCallback();
        };
    }, [handleView, editor]);


    return (
        <SidePane
            id={panelId}
            openInitially
            closeAllOpened
            title={continuousPipe.ref}
            titleLabel={<FormattedMessage id="continuouspipe-panel-title" defaultMessage="Continuous Pipe" />}
            titleIcon={<PipeIcon />}
            titleOnView={handleView}
            actions={<NavigateBackButton />}
        >
            <Box height="100%" justify="between">
                <Box flex>
                    <UpperContainer>
                        <HorizontalLine color="grey" />
                        <ContinuousPipeAttributes
                            continuousPipe={continuousPipe}
                            onView={handleView}
                        />
                        <HorizontalLine color="grey" />
                        <Box direction="row">
                            <ContinuousPipeActions
                                continuousPipe={continuousPipe}
                                map={networkMap}
                                onView={handleView}
                                editor={editor}
                                onEdit={updateObject}
                            />
                        </Box>
                        <HorizontalLine color="grey" />
                    </UpperContainer>
                    <BottomContainer>
                        <ContinuousPipeContent
                            continuousPipe={continuousPipe}
                            editor={editor}
                            clickedRow={clickedRow}
                            setClickedRow={setClickedRow}
                            map={networkMap}
                        />
                    </BottomContainer>
                </Box>
            </Box>
        </SidePane>
    );
};

export default withLeaflet(ContinuousPipe);
